import { useCallback, useEffect, useState } from 'react';
import { ApiError, type Api, type CallRow, type DncRow } from '../api';
import type { T } from '../i18n';
import { PERM, useCan } from '../access';
import { ReadOnlyNote } from '../ui';
import { CallCard } from './CallDetail';
import { ScoreBadge } from './Leads';
import { WebChats } from './WebChats';

type Tab = 'all' | 'callbacks' | 'web' | 'dnc';

export const duration = (s: number | null): string => (s === null ? '—' : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`);
/** The other party of a call; a test room (ADR-035) shows as such, never as a number. */
export const partyOf = (c: { direction: string; to?: string | null; from?: string | null }, t: T): string => {
  const x = c.direction === 'outbound' ? c.to : c.from;
  // ADR-076: a website chat's record carries its site, not a number.
  if (x?.startsWith('web-chat:')) return t('calls.party.webChat');
  return x === 'test-room' ? t('room.title') : (x ?? '');
};
export const when = (iso: string): string => new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });

/**
 * BRAIN/34 P2: the other party as the contact's name (when the number is in contacts) above the number. The name is
 * isolated (<bdi>) so a Latin name reads right in Arabic and the other way round; the number keeps its own direction.
 */
export const PartyLabel = ({ t, row }: { t: T; row: { direction: string; to?: string | null; from?: string | null; contact?: { name: string } | null } }) => {
  const number = partyOf(row, t);
  const name = row.contact?.name?.trim();
  if (!number && row.direction === 'inbound') return <span className="muted">{t('calls.party.none')}</span>;
  return name ? (
    <span className="party">
      <bdi className="party-name">{name}</bdi>
      <span className="mono num">{number}</span>
    </span>
  ) : (
    <span className="mono num">{number}</span>
  );
};

/** BRAIN/34 P2: search by name, number (spaces ignored) or agent; accents and Arabic vowel marks do not matter (no language table). */
const fold = (s: string): string => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
export const matchesQuery = (c: { to?: string | null; from?: string | null; agentName?: string; contact?: { name: string } | null }, q: string): boolean => {
  const text = fold(q.trim());
  if (!text) return true;
  const digits = q.replace(/\D/gu, '');
  const numbers = `${c.to ?? ''} ${c.from ?? ''}`;
  return (
    fold(c.contact?.name ?? '').includes(text) ||
    fold(c.agentName ?? '').includes(text) ||
    (digits.length > 0 && numbers.replace(/\D/gu, '').includes(digits)) ||
    numbers.replace(/\s+/gu, '').toLowerCase().includes(text.replace(/\s+/gu, ''))
  );
};

/** ADR-027: where an agreed callback stands (automatic call planned / made / not possible, or a list item for the team). */
export const callbackStateOf = (c: Pick<CallRow, 'callbackAt' | 'callbackStatus' | 'callbackError'>, t: T): { text: string; kind: string } => {
  switch (c.callbackStatus) {
    case 'scheduled':
    case 'dialing':
      return { text: t('callback.state.scheduled', { at: c.callbackAt ? when(c.callbackAt) : '' }), kind: 'published' };
    case 'dialed':
      return { text: t('callback.state.dialed'), kind: '' };
    case 'expired':
      return { text: t('callback.state.expired'), kind: 'fail' };
    case 'failed':
    case 'skipped': {
      const code = c.callbackError ?? '';
      const reason = t(`error.${code}`) === `error.${code}` ? code : t(`error.${code}`);
      return { text: t('callback.state.notCalled', { reason }), kind: 'fail' };
    }
    default:
      return { text: t('callback.state.manual'), kind: 'muted' };
  }
};

/** One short label for how the call ended (the business's view, not the technical status). */
export const resultOf = (c: Pick<CallRow, 'status' | 'endedBy' | 'outcome' | 'durationSeconds'>, t: T): { text: string; kind: string } => {
  const o = c.outcome;
  if (o?.answeredBy === 'machine') return { text: t('calls.result.machine'), kind: 'muted' };
  if (o?.optedOut) return { text: t('calls.result.optedOut'), kind: 'fail' };
  if (o?.callback) return { text: t('calls.result.callback', { when: o.callback.when }), kind: 'published' };
  if (c.status === 'live') return { text: t('calls.result.live'), kind: 'published' };
  if (c.status === 'created') return { text: t('calls.result.notAnswered'), kind: 'muted' };
  if (c.endedBy === 'error') return { text: t('calls.result.error'), kind: 'fail' };
  return { text: t('calls.result.talked'), kind: '' };
};

export const CallsPage = ({
  api, t, initialTab, initialCall, initialSameAs, onOpen, onTab, onSameNumber, onClearSameAs,
}: {
  api: Api; t: T; initialTab: Tab; initialCall?: string | undefined; initialSameAs?: string | undefined; onOpen: (id: string) => void; onTab: (tab: Tab) => void;
  /** BRAIN/34 P2: show only the calls with the same number as this call / back to all calls. */
  onSameNumber?: (callId: string) => void; onClearSameAs?: () => void;
}) => {
  // ADR-071: marking a callback done is a calling act (centeros.call); the list itself is for everyone who may see.
  const canCall = useCan()(PERM.call);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [rows, setRows] = useState<CallRow[]>();
  const [error, setError] = useState<string>();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<'all' | 'today' | 'callback' | 'recorded' | 'machine' | 'optedOut'>('all');
  const [selected, setSelected] = useState<string | undefined>(initialCall);
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.code : 'generic');

  const load = useCallback(() => {
    if (tab === 'dnc' || tab === 'web') return;
    setRows(undefined);
    api.calls(tab === 'callbacks' ? 'callbacks' : 'all', tab === 'all' && initialSameAs ? { sameAs: initialSameAs } : undefined).then((r) => setRows(r.calls)).catch(fail);
  }, [api, tab, initialSameAs]);
  useEffect(load, [load]);

  const pick = (x: Tab) => {
    setTab(x);
    setError(undefined);
    onTab(x);
  };

  const done = async (id: string) => {
    try {
      await api.callbackDone(id);
      load();
    } catch (e) {
      fail(e);
    }
  };

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('calls.title')}</h1>
          <p className="muted">{t('calls.subtitle')}</p>
        </div>
      </header>
      <div className="tabs">
        {(['all', 'callbacks', 'web', 'dnc'] as const).map((x) => (
          <button key={x} type="button" className={`tab ${tab === x ? 'active' : ''}`} onClick={() => pick(x)}>{t(`calls.tab.${x}`)}</button>
        ))}
      </div>
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? `${t('error.generic')} (${error})` : t(`error.${error}`)}</div>}
      {tab === 'dnc' && <DoNotCallPanel api={api} t={t} />}
      {tab === 'web' && <WebChats api={api} t={t} onOpenCall={onOpen} />}
      {tab === 'all' && initialSameAs && (
        <div className="banner info small row-between">
          <span>{t('calls.filter.sameNumber')}</span>
          {onClearSameAs && <button type="button" className="link" onClick={onClearSameAs}>{t('calls.filter.clear')}</button>}
        </div>
      )}
      {tab !== 'dnc' && tab !== 'web' && !rows && !error && <div className="muted">{t('boot.loading')}</div>}
      {tab !== 'dnc' && tab !== 'web' && rows && rows.length === 0 && <div className="empty"><p>{t(tab === 'callbacks' ? 'calls.callbacks.empty' : 'calls.empty')}</p></div>}
      {tab === 'all' && rows && rows.length > 0 && (() => {
        const shown = rows
          .filter((c) => matchesQuery(c, query))
          .filter((c) => {
            const o = c.outcome;
            if (kind === 'today') return new Date(c.createdAt).toDateString() === new Date().toDateString();
            if (kind === 'callback') return Boolean(o?.callback);
            if (kind === 'recorded') return c.hasRecording;
            if (kind === 'machine') return o?.answeredBy === 'machine';
            if (kind === 'optedOut') return Boolean(o?.optedOut);
            return true;
          });
        const current = selected && rows.some((c) => c.id === selected) ? selected : shown[0]?.id;
        return (
          <div className="split">
            <div className="split-list">
              <div className="kicker">{t('calls.list.kicker')}</div>
              <h3>{t('calls.list.title')}</h3>
              <input className="search" type="search" value={query} placeholder={t('calls.search')} onChange={(e) => setQuery(e.target.value)} />
              <div className="chips">
                {(['all', 'today', 'callback', 'recorded', 'machine', 'optedOut'] as const).map((k) => (
                  <button key={k} type="button" className={`chip ${kind === k ? 'active' : ''}`} onClick={() => setKind(k)}>{t(`calls.kind.${k}`)}</button>
                ))}
              </div>
              <div className="call-items">
                {shown.length === 0 && <p className="muted small">{t('calls.none')}</p>}
                {shown.map((c) => {
                  const r = resultOf(c, t);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`call-item ${current === c.id ? 'active' : ''}`}
                      onClick={() => (matchMedia('(max-width: 960px)').matches ? onOpen(c.id) : setSelected(c.id))}
                    >
                      <span className="row-between">
                        <span className="dir">
                          {t(`direction.${c.direction}`)}
                          {(c.inboundWindow === 'closed' || c.inboundWindow === 'holiday') && <span className="pill muted small-pill"> {t(c.inboundWindow === 'closed' ? 'calls.afterHours' : 'calls.closedDay')}</span>}
                        </span>
                        <span className="row"><ScoreBadge score={c.analysis?.score} t={t} /><span className={`pill ${r.kind}`}>{r.text}</span></span>
                      </span>
                      <PartyLabel t={t} row={c} />
                      <span className="hint">
                        {when(c.createdAt)} · {duration(c.durationSeconds)} · {c.agentName}{c.hasRecording ? ' · ●' : ''}
                        {(c.priorCalls ?? 0) > 0 && <span className="prior-badge" title={t('calls.prior', { n: c.priorCalls ?? 0 })} aria-label={t('calls.prior', { n: c.priorCalls ?? 0 })}> · ↺ {c.priorCalls}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="split-detail">{current ? <CallCard key={current} api={api} t={t} callId={current} onOpen={(id) => (rows.some((c) => c.id === id) ? setSelected(id) : onOpen(id))} {...(onSameNumber ? { onSameNumber } : {})} /> : <div className="empty"><p>{t('calls.pick')}</p></div>}</div>
          </div>
        );
      })()}
      {tab === 'callbacks' && rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>{t('calls.col.number')}</th><th>{t('calls.col.callbackWhen')}</th><th>{t('calls.col.note')}</th><th>{t('calls.col.callbackState')}</th><th>{t('calls.col.agent')}</th>{canCall && <th />}</tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id}>
                <td className="clickable" onClick={() => onOpen(c.id)}><PartyLabel t={t} row={c} /></td>
                <td><strong>{c.outcome?.callback?.when}</strong></td>
                <td>{c.outcome?.callback?.note ?? ''}</td>
                <td>{((s) => <span className={`pill ${s.kind}`}>{s.text}</span>)(callbackStateOf(c, t))}</td>
                <td>{c.agentName}<div className="muted small">{when(c.createdAt)}</div></td>
                {canCall && <td><button type="button" className="secondary" onClick={() => void done(c.id)}>{t('calls.callbacks.done')}</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
};

const DoNotCallPanel = ({ api, t }: { api: Api; t: T }) => {
  // ADR-071: adding a number is a contacts act; taking one off the list is a compliance act (an editor may add, not remove).
  const can = useCan();
  const canAdd = can(PERM.contacts);
  const canRemove = can(PERM.compliance);
  const [rows, setRows] = useState<DncRow[]>();
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.doNotCall().then((r) => setRows(r.numbers)).catch((e: unknown) => setError(e instanceof ApiError ? e.code : 'generic'));
  }, [api]);
  useEffect(load, [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await fn();
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'generic');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <p className="muted">{t('dnc.hint')}</p>
      <ReadOnlyNote t={t} need={PERM.contacts} />
      {canAdd && (
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (phone.trim()) void run(async () => { await api.addDoNotCall(phone.trim()); setPhone(''); });
        }}
      >
        <input type="tel" value={phone} placeholder={t('dnc.placeholder')} onChange={(e) => setPhone(e.target.value)} aria-label={t('dnc.placeholder')} />
        <button type="submit" className="primary" disabled={busy || !phone.trim()}>{t('dnc.add')}</button>
      </form>
      )}
      {error && <div className="banner error">{t(`error.${error}`) === `error.${error}` ? `${t('error.generic')} (${error})` : t(`error.${error}`)}</div>}
      {rows && rows.length === 0 && <div className="empty"><p>{t('dnc.empty')}</p></div>}
      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr><th>{t('calls.col.number')}</th><th>{t('dnc.col.reason')}</th><th>{t('calls.col.date')}</th>{canRemove && <th />}</tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.e164}>
                <td className="mono">{r.e164}</td>
                <td>{t(`dnc.reason.${r.reason}`) === `dnc.reason.${r.reason}` ? r.reason : t(`dnc.reason.${r.reason}`)}</td>
                <td className="muted">{when(r.createdAt)}</td>
                {canRemove && <td><button type="button" className="secondary" disabled={busy} onClick={() => void run(() => api.removeDoNotCall(r.e164))}>{t('dnc.remove')}</button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
